import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  TextareaAutosize,
  setRef,
} from "@mui/material";
import { AppSingleton } from "../services/user_config";
import * as poseDetection from "@tensorflow-models/pose-detection";
import * as tf from "@tensorflow/tfjs-core";
import "@tensorflow/tfjs-backend-webgl";
import { cosDistance, drawLines, flipHorizontally } from "../utils/PoseUitils";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

const boneNameDict = {
  mixamorigRightEye: "leftEye",
  mixamorigLeftEye: "rightEye",
  mixamorigLeftShoulder: "rightShoulder",
  mixamorigLeftForeArm: "rightElbow",
  mixamorigLeftHand: "rightWrist",
  mixamorigRightShoulder: "leftShoulder",
  mixamorigRightForeArm: "leftElbow",
  mixamorigRightHand: "leftWrist",
  mixamorigLeftUpLeg: "rightHip",
  mixamorigLeftLeg: "rightKnee",
  mixamorigLeftFoot: "rightAnkle",
  mixamorigRightUpLeg: "leftHip",
  mixamorigRightLeg: "leftKnee",
  mixamorigRightFoot: "leftAnkle",
};

const missingJoints = [
  "mixamorigHips",
  "mixamorigSpine",
  "mixamorigSpine1",
  "mixamorigSpine2",
  "mixamorigNeck",
  "mixamorigHead",
  "mixamorigHeadTop_End",
  "mixamorigLeftArm",
  "mixamorigLeftHandMiddle1",
  "mixamorigLeftHandMiddle2",
  "mixamorigLeftHandMiddle3",
  "mixamorigLeftHandMiddle4",
  "mixamorigLeftHandThumb1",
  "mixamorigLeftHandThumb2",
  "mixamorigLeftHandThumb3",
  "mixamorigLeftHandThumb4",
  "mixamorigLeftHandIndex1",
  "mixamorigLeftHandIndex2",
  "mixamorigLeftHandIndex3",
  "mixamorigLeftHandIndex4",
  "mixamorigLeftHandRing1",
  "mixamorigLeftHandRing2",
  "mixamorigLeftHandRing3",
  "mixamorigLeftHandRing4",
  "mixamorigLeftHandPinky1",
  "mixamorigLeftHandPinky2",
  "mixamorigLeftHandPinky3",
  "mixamorigLeftHandPinky4",
  "mixamorigRightArm",
  "mixamorigRightHandMiddle1",
  "mixamorigRightHandMiddle2",
  "mixamorigRightHandMiddle3",
  "mixamorigRightHandMiddle4",
  "mixamorigRightHandThumb1",
  "mixamorigRightHandThumb2",
  "mixamorigRightHandThumb3",
  "mixamorigRightHandThumb4",
  "mixamorigRightHandIndex1",
  "mixamorigRightHandIndex2",
  "mixamorigRightHandIndex3",
  "mixamorigRightHandIndex4",
  "mixamorigRightHandRing1",
  "mixamorigRightHandRing2",
  "mixamorigRightHandRing3",
  "mixamorigRightHandRing4",
  "mixamorigRightHandPinky1",
  "mixamorigRightHandPinky2",
  "mixamorigRightHandPinky3",
  "mixamorigRightHandPinky4",
  "mixamorigLeftToeBase",
  "mixamorigLeftToe_End",
  "mixamorigRightToeBase",
  "mixamorigRightToe_End",
  "Shoes",
  "Tops",
  "Bottoms",
  "Hair",
  "Body",
  "Eyelashes",
  "Eyes",
];

const findBoneByName = (object, name) => {
  let bone = null;
  object.traverse((node) => {
    // console.log(node.name);
    if (node.isBone && node.name === name) {
      bone = node;
    }
  });
  // console.log("findBoneByName");
  // console.log(bone);
  return bone;
};

const mirrorModel = ({ modelRef, rendererRef, sceneRef, cameraRef }) => {
  console.log("mirrorModel");
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(
    75, // MARK: fov (field of view)
    window.innerWidth / window.innerHeight, // MARK: aspect (aspect ratio)
    0.1, // MARK: near (near clipping plane)
    1000 // MARK: far (far clipping plane)
  );
  camera.position.z = 5;

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(window.innerWidth, window.innerHeight);
  document.body.appendChild(renderer.domElement);

  const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
  scene.add(ambientLight);

  const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
  directionalLight.position.set(1, 1, 1).normalize();
  scene.add(directionalLight);

  const loader = new GLTFLoader();
  loader.load(
    "../models/Walking.gltf",
    (gltf) => {
      // const jointName = "mixamorigLeftArm";
      // const joint = findBoneByName(gltf.scene, jointName);
      console.log("Model Init");
      // console.log(jointName);
      // console.log(joint.position);
      // if (joint) {
      //   joint.position.set(10, 10, 10); // Set the position of the joint
      //   joint.rotation.set(0, 0, 0); // Set the rotation of the joint
      //   joint.scale.set(1, 1, 1); // Set the scale of the joint
      // }
      renderer.render(scene, camera);

      modelRef.current = gltf.scene;
      scene.add(gltf.scene);
      rendererRef.current = renderer;
      sceneRef.current = scene;
      cameraRef.current = camera;
    },
    undefined,
    (error) => {
      console.error("Error loading GLTF model:", error);
    }
  );

  // Render the scene
  const animate = () => {
    requestAnimationFrame(animate);
    renderer.render(scene, camera);
  };
  animate();
};

const AppContainer = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const modelRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const [detector, setDetector] = useState(null);
  const [isTfReady, setIsTfReady] = useState(false);
  const [isModelReady, setIsModelReady] = useState(false);
  const [tfMemoryInfo, setTfMemoryInfo] = useState({});
  const [isPoseOnly, setIsPoseOnly] = useState(false);
  const [logText, setLogText] = useState("");
  const [newText, setNewText] = useState("");
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [poseKeyPointDict, setPoseKeyPointDict] = useState({});
  const [modelJoints, setModelJoints] = useState({});
  const realShoulderLengthRef = useRef(null);
  const modelShoulderLengthRef = useRef(null);
  const realAnchorNose = useRef(null);
  const modelAnchorHead = useRef(null);

  const tryAnchor = ({ realPoseDict }) => {
    if (Object.keys(realPoseDict).length > 0) {
      // MARK: Anchor real nose position
      if (realAnchorNose.current === null) {
        realAnchorNose.current = realPoseDict.nose;
      }
      // MARK: Get real shoulder lengnth
      // console.log(realPoseDict.shoulderLength);
      if (
        realShoulderLengthRef.current === null &&
        !isNaN(realPoseDict.shoulderLength)
      ) {
        realShoulderLengthRef.current = realPoseDict.shoulderLength;
      }
    }

    if (modelRef.current !== null) {
      // MARK: Anchor model head position
      if (modelAnchorHead.current === null) {
        const _modelHead = findBoneByName(modelRef.current, "mixamorigHead");
        modelAnchorHead.current = _modelHead.position;
      }
      // MARK: Get model shoulder lengnth
      if (modelShoulderLengthRef.current === null) {
        const _modelLeftShouder = findBoneByName(
          modelRef.current,
          "mixamorigRightShoulder"
        );
        const _modelRightShouder = findBoneByName(
          modelRef.current,
          "mixamorigLeftShoulder"
        );
        const _modelShoulderLength = cosDistance(
          _modelLeftShouder.position,
          _modelRightShouder.position
        );
        // console.log("_modelLeftShouder");
        // console.log(_modelLeftShouder);
        // console.log("_modelRightShouder");
        // console.log(_modelRightShouder);
        modelShoulderLengthRef.current = _modelShoulderLength;
      }
    }
  };
  useEffect(() => {
    if (isFirstLoad) {
      mirrorModel({ modelRef, rendererRef, sceneRef, cameraRef });
      setIsModelReady(true);
    }
    setIsFirstLoad(false);
  }, [isFirstLoad]);

  useEffect(() => {
    tryAnchor({ realPoseDict: poseKeyPointDict });
    if (
      modelRef.current &&
      realShoulderLengthRef.current !== null &&
      modelShoulderLengthRef.current !== null &&
      realAnchorNose.current !== null &&
      modelAnchorHead.current !== null
    ) {
      const coordinateDisplacement = {
        x: modelAnchorHead.current.x - realAnchorNose.current.x,
        y: modelAnchorHead.current.y - realAnchorNose.current.y,
      };

      const skeletonRatio =
        modelShoulderLengthRef.current / realShoulderLengthRef.current;
      Object.keys(boneNameDict).forEach((jointName) => {
        const joint = findBoneByName(modelRef.current, jointName);
        if (joint) {
          joint.position.set(
            (poseKeyPointDict[boneNameDict[jointName]].x +
              coordinateDisplacement.x) *
              skeletonRatio,
            (poseKeyPointDict[boneNameDict[jointName]].y +
              coordinateDisplacement.y) *
              skeletonRatio,
            0.0
          );
        }
      });
    }
  }, [poseKeyPointDict]);

  useEffect(() => {
    if (newText.length > 0) {
      setLogText(logText + `\n${newText}`);
    }
  }, [newText, logText]);

  const startCamera = async () => {
    setNewText("startCamera");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        // video: true,
        video: { facingMode: "user" },
      });
      // setNewText("await navigator.mediaDevices done");
      if (videoRef.current) {
        setNewText("video reference found");
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (error) {
      setNewText(`Error accessing camera: ${error}`);
      console.error("Error accessing camera:", error);
    }
  };

  const startCapture = () => {
    setNewText("startCapture");
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const captureFrame = async () => {
      context.clearRect(0, 0, canvas.width, canvas.height);
      if (!isPoseOnly) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
      }
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      // flipHorizontally({ imageData, canvas, context });
      const convertedImageData = new ImageData(
        imageData.data,
        canvas.width,
        canvas.height
      );
      const tensor = tf.browser.fromPixels(convertedImageData);
      const poses = await detector.estimatePoses(tensor, {
        flipHorizontal: true,
        decodingMethod: "single-person",
        maxPoseDetections: 1,
        scoreThreshold: 0.5,
        minPoseConfidence: 0.3,
      });
      setTfMemoryInfo(tf.memory());
      tf.dispose(tensor);
      if (poses.length > 0) {
        const _poseKeyPointDict = drawLines({ poses, context });
        // console.log(Object.keys(_poseKeyPointDict));
        if (Object.keys(_poseKeyPointDict).length > 0) {
          setPoseKeyPointDict(_poseKeyPointDict);
        }
      }
      animationFrameRef.current = requestAnimationFrame(captureFrame);
    };

    // Start capturing frames
    try {
      const animate = async () => {
        await captureFrame();
      };
      animationFrameRef.current = requestAnimationFrame(animate);
    } catch (error) {
      setNewText(`captureFrame Error: ${error}`);
    }
  };

  useEffect(() => {
    const initializePoseDetector = async () => {
      await tf.setBackend("webgl"); // Set the backend to 'webgl' for better performance
      await tf.ready(); // Wait for TensorFlow.js to initialize the backend
      setIsTfReady(true);
      setNewText("tf.ready");
      const detectorConfig = {
        modelType: poseDetection.movenet.modelType.SINGLEPOSE_LIGHTNING,
      };
      const _detector = await poseDetection.createDetector(
        poseDetection.SupportedModels.MoveNet,
        detectorConfig
      );
      setDetector(_detector);
      console.log("Detector init");
    };
    initializePoseDetector();
    startCamera();
  }, []);

  return (
    <Box>
      Header
      <Box>
        <video
          ref={videoRef}
          // width="640"
          // height="480"
          autoPlay={true}
          playsInline={true}
          muted={true}
          style={{ display: "none" }}
        />
        <canvas ref={canvasRef} />
      </Box>
      <Button
        disabled={!isTfReady || !isModelReady || !detector}
        onClick={startCapture}
      >
        Start
      </Button>
      {/* <Button
        onClick={() => {
          setMemoryInfo(window.performance.memory);
        }}
      >
        Get Memory Info
      </Button> */}
      <Button
        onClick={() => {
          setIsPoseOnly(!isPoseOnly);
        }}
      >
        Show / Hide Picture
      </Button>
      <TextField>Physical Length: {realShoulderLengthRef.current}</TextField>
      <TextField>Model Length: {modelShoulderLengthRef.current}</TextField>
      <TextField
        disabled
        // value={`Heap: ${memoryInfo?.usedJSHeapSize} / ${memoryInfo?.totalJSHeapSize} (Limited: ${memoryInfo?.jsHeapSizeLimit})`}
        value={`Tensors: ${tfMemoryInfo?.numTensors ?? 0}\tGPU: ${
          (tfMemoryInfo?.numBytesInGPU ?? 0) / 1000000
        } / ${(tfMemoryInfo?.numBytesInGPUAllocated ?? 0) / 1000000} (${
          (tfMemoryInfo?.numBytesInGPUFree ?? 0) / 1000000
        })`}
        sx={{ width: "100%" }}
        // value={`${tf.memory()}}`}
      ></TextField>
    </Box>
  );
};

const f = () => {};

export { AppContainer, f };
